import { FileActionButton } from "../file-action/file-action-controls";
import React, { useEffect, useState, useCallback } from 'react';
import { RawMaterialPurchaseListDTO } from "../../models/RawMaterials/RawMaterial-model";
import { t } from 'i18next';
import RawMaterialsSearch from './RawMaterial-Search';
import RawMaterialsDatatable from './RawMaterialDatatable';
import RawMaterialService from '../../services/RawMaterials/RawMaterialService';
import { rawMaterialExport } from './RawmaterialList-Export';

const RawMaterialsPage: React.FC = () => {
  const [materials, setMaterials] = useState<RawMaterialPurchaseListDTO[]>([]);
  const [qcStatuses, setQcStatuses] = useState<any[]>([]);

  const [currentSortField, setCurrentSortField] = useState<string | undefined>(undefined);
  const [currentSortOrder, setCurrentSortOrder] = useState<1 | -1 | null>(null);

  // ✅ userPosId: stores the logged-in user's mapped POS
  const [userPosId, setUserPosId] = useState<number | null>(null);
  const [posLoaded, setPosLoaded] = useState(false);

  const [filters, setFilters] = useState({
    rawMaterialId: undefined as number | undefined,
    rmCategoryId: undefined as number | undefined,
    posId: undefined as number | undefined,
    activeOnly: true,
    sortColumn: 'createdDate',
    sortDirection: 'desc',
    keyword: undefined as string | undefined,
    invoiceNumber: undefined as string | undefined,
    posAlias: undefined as string | undefined,
    startDate: undefined as string | Date | undefined,
    endDate: undefined as string | Date | undefined
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Step 1: Load user's POS on mount — only once
  useEffect(() => {
    const loadUserPos = async () => {
      try {
        const units = await RawMaterialService.GetManufacrturingunitlist();
        console.log("🏭 Manufacturing units returned:", units);
        if (Array.isArray(units) && units.length > 0) {
          console.log("✅ Setting userPosId to:", units[0].PosID);
          setUserPosId(units[0].PosID);
        } else {
          console.warn("⚠️ No units returned or empty array");
        }
      } catch (e) {
        console.error("Failed to load user POS", e);
      } finally {
        setPosLoaded(true);
      }
    };
    loadUserPos();
  }, []);

  const fetchMaterials = useCallback(async (overrideFilters?: any) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const finalFilters = overrideFilters || filters;

      const payload = {
        pageNumber,
        pageSize,
        rawMaterialId: finalFilters.rawMaterialId,
        rmCategoryId: finalFilters.rmCategoryId,
        // ✅ Always use userPosId — never let filters override it
        posId: userPosId ?? null,
        sortColumn: finalFilters.sortColumn,
        sortDirection: finalFilters.sortDirection,
        keyword: finalFilters.keyword,
        invoiceNumber: finalFilters.invoiceNumber,
        posAlias: finalFilters.posAlias,
        startDate: finalFilters.startDate,
        endDate: finalFilters.endDate,
      };

      console.log("📤 Fetching with payload:", payload);

      const response = await RawMaterialService.getRawMaterialPurchases(payload);

      const newMaterials = response.rawMaterialPurchases?.map((item: any) => ({ ...item })) || [];
      const newQcStatuses = response.qcStatuses?.map((item: any) => ({ ...item })) || [];

      setMaterials(newMaterials);
      setTotalRecords(response.totalRecords || 0);
      setQcStatuses(newQcStatuses);

    } catch (error) {
      console.error("❌ Error loading materials:", error);
      setMaterials([]);
      setTotalRecords(0);
      setQcStatuses([]);
    } finally {
      setIsLoading(false);
    }
  }, [pageNumber, pageSize, filters, userPosId]); // ✅ userPosId in deps

  // ✅ Step 2: Only fetch after POS has loaded
  useEffect(() => {
    if (!posLoaded) return;
    fetchMaterials();
  }, [pageNumber, pageSize, posLoaded]); // intentionally limited deps here

  const handleSearch = useCallback((searchFilters: any) => {
    console.log("🔍 handleSearch called with:", searchFilters);

    const updatedFilters = {
      ...filters,
      rawMaterialId: searchFilters.rawMaterialId,
      rmCategoryId: searchFilters.rmCategoryId,
      // ✅ Do NOT allow search to override posId — we always use userPosId
      keyword: searchFilters.keyword,
      invoiceNumber: searchFilters.invoiceNumber,
      posAlias: searchFilters.posAlias,
      startDate: searchFilters.startDate,
      endDate: searchFilters.endDate
    };

    setFilters(updatedFilters);
    setPageNumber(1);
    fetchMaterials(updatedFilters);
  }, [filters, fetchMaterials]);

  const handleSortChange = (field: string, order: 1 | -1) => {
    console.log("📊 Parent handleSortChange:", { field, order });

    const columnMapping: Record<string, string> = {
      invoiceNumber: "invoiceNumber",
      invoiceId: "invoiceNumber",
      createdbyName: "createdByName",
      rawMaterialName: "rawMaterialName",
      categoryName: "categoryName",
      vendorName: "vendorName",
      purchaseDate: "purchaseDate",
      quantity: "quantity",
      quantityWithUnit: "quantity",
      basicAmount: "basicAmount",
      taxAmount: "taxAmount",
      totalAmount: "totalAmount",
      createdDate: "createdDate",
      updatedByName: "updatedByName",
      updatedDate: "updatedDate",
      posAlias: "posAlias",
      acceptedQuantity: "acceptedQuantity",
      rejectedQuantity: "rejectedQuantity",
      remainingQuantity: "remainingQuantity"
    };

    const mappedField = columnMapping[field] || field;

    setCurrentSortField(field);
    setCurrentSortOrder(order);

    setFilters(prevFilters => ({
      ...prevFilters,
      sortColumn: mappedField,
      sortDirection: order === 1 ? "asc" : "desc"
    }));
  };

  const handleExport = async () => {
    try {
      const payload = {
        pageNumber: 1,
        pageSize: totalRecords,
        rawMaterialId: filters.rawMaterialId,
        rmCategoryId: filters.rmCategoryId,
        posId: userPosId ?? null, // ✅ use userPosId for export too
        startDate: filters.startDate,
        endDate: filters.endDate,
        activeOnly: filters.activeOnly,
        sortColumn: filters.sortColumn,
        sortDirection: filters.sortDirection,
        keyword: filters.keyword,
        invoiceNumber: filters.invoiceNumber,
        posAlias: filters.posAlias,
      };

      await rawMaterialExport(payload);
    } catch (error) {
      console.error("Error exporting raw materials:", error);
    }
  };

  const handleReset = () => {
    setCurrentSortField(undefined);
    setCurrentSortOrder(null);

    const resetFilters = {
      rawMaterialId: undefined,
      activeOnly: true,
      rmCategoryId: undefined,
      posId: undefined, // ✅ posId in filters doesn't matter — userPosId is always used
      sortColumn: 'createdDate',
      sortDirection: 'desc',
      keyword: undefined,
      invoiceNumber: undefined,
      posAlias: undefined,
      startDate: undefined,
      endDate: undefined
    };

    setFilters(resetFilters);
    setPageNumber(1);
    fetchMaterials(resetFilters);
  };

  const handlePageChange = (event: any) => {
    setPageNumber(event.page + 1);
    setPageSize(event.rows);
  };

  const handleUpdate = useCallback((material: RawMaterialPurchaseListDTO) => {
    console.log("🔄 handleUpdate called");
    setTimeout(() => {
      fetchMaterials();
    }, 300);
  }, [fetchMaterials]);

  const handleRefresh = useCallback(() => {
    console.log("🔄 handleRefresh called");
    fetchMaterials();
  }, [fetchMaterials]);

  return (
    <div className="p-3">
      <div className="col-12">
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
          <h4 className="ml-1 mb-0">
            {t("Raw Material Quality Check")}
          </h4>
          <FileActionButton action="export"
            className="text-black border-round mr-2 mb-1"
            onClick={handleExport}
            icon="pi pi-file-export mr-1"
            text
            raised
            tooltip="Export to Excel"
            tooltipOptions={{ position: 'top' }}
            style={{ width: "auto", marginLeft: "10px" }}
          >
            Export All Raw Materials
          </FileActionButton>
        </div>
      </div>

      <RawMaterialsSearch
        onSearch={handleSearch}
        onReset={handleReset}
        totalRecords={totalRecords}
      />

      {/* ✅ removed key={refreshKey} — no more remount flicker */}
      <RawMaterialsDatatable
        data={materials}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onUpdate={handleUpdate}
        qcStatuses={qcStatuses}
        onRefresh={handleRefresh}
        sortField={currentSortField}
        sortOrder={currentSortOrder}
        isLoading={isLoading}
      />
    </div>
  );
};

export default RawMaterialsPage;